import ActionCable from "actioncable";
import * as token from "./token";
import { ensureValidToken } from "./refresh";
import { getWebSocketUrl } from "@/utils/urlHelper";

// Cache da conexão WebSocket
let cableConnection = null;
let reconnectTimer = null;

// Cria uma nova conexão WebSocket com token válido
const createConnection = function() {
  // Debug: check token directly from storage first
  const directToken = token.get();
  console.log('🔧 WebSocket DEBUG: Token direto do storage:', directToken ? directToken.substring(0, 50) + '...' : 'NULL');

  return ensureValidToken().then(function(validToken) {
    console.log('🔒 WebSocket: Token bruto recebido:', validToken ? validToken.substring(0, 50) + '...' : 'NULL');

    if (!validToken || validToken === false || validToken === 'false') {
      throw new Error('No valid token available for WebSocket connection');
    }

    // Limpar o token se vier com "Bearer "
    const cleanToken = validToken.replace(/^Bearer\s+/, '');
    console.log('🔒 WebSocket: Token limpo para uso:', cleanToken ? cleanToken.substring(0, 50) + '...' : 'NULL');

    if (!cleanToken || cleanToken.trim() === '') {
      throw new Error('Token is empty after cleaning');
    }

    // Usar o helper para obter a URL do WebSocket correta
    const baseWebSocketUrl = getWebSocketUrl();
    const websocketUrl = baseWebSocketUrl + '?token=' + encodeURIComponent(cleanToken);

    console.log('🔗 WebSocket: URL completa gerada:', websocketUrl.replace(/token=[^&]+/, 'token=' + (cleanToken ? cleanToken.substring(0, 20) + '...' : 'EMPTY')));
    console.log('🔗 WebSocket: URL completa SEM mascaramento:', websocketUrl);
    
    const cable = ActionCable.createConsumer(websocketUrl);
    
    // Monitorar estado da conexão
    if (cable.connection.monitor && typeof cable.connection.monitor.addEventListener === 'function') {
      cable.connection.monitor.addEventListener('connected', function() {
        console.log('✅ WebSocket connected successfully');
        // Limpar timer de reconnect se existir
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
      });

      cable.connection.monitor.addEventListener('disconnected', function() {
        console.log('❌ WebSocket disconnected - connection lost');
        // Marcar a conexão como inválida para forçar nova criação
        cableConnection = null;

        // Tentar reconectar após um delay, mas só se não estivermos na página de login
        if (!reconnectTimer && window.location.pathname !== '/login') {
          reconnectTimer = setTimeout(function() {
            console.log('🔄 Attempting WebSocket reconnection with fresh token...');
            reconnectWebSocket().catch(function(error) {
              console.error('Failed to reconnect WebSocket:', error);
            });
            reconnectTimer = null;
          }, 30000); // Ajustado para 30 segundos
        }
      });

      cable.connection.monitor.addEventListener('rejected', function() {
        console.log('🚫 WebSocket connection rejected - token may be expired');
        // Marcar conexão como inválida
        cableConnection = null;

        // Token expirado, tentar refresh e reconectar
        setTimeout(function() {
          reconnectWebSocket().catch(function(error) {
            console.error('Failed to reconnect after rejection:', error);
            // Se falhar, redirecionar para login
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          });
        }, 1000);
      });
    } else {
      console.warn('⚠️ WebSocket monitor not available, connection events will not be tracked');
    }

    return cable;
  }).catch(function(error) {
    console.error('Failed to create WebSocket connection:', error);
    throw error;
  });
};

// Obtém a conexão WebSocket atual ou cria uma nova
export const getWebSocketConnection = function() {
  if (!cableConnection) {
    console.log('🔗 Getting new WebSocket connection...');
    return createConnection().then(function(connection) {
      cableConnection = connection;
      return cableConnection;
    }).catch(function(error) {
      console.error('Failed to get WebSocket connection:', error);
      throw error;
    });
  }

  // Se já existe uma conexão, verificar se ainda é válida
  if (cableConnection.connection && cableConnection.connection.isOpen()) {
    console.log('🔗 Using existing WebSocket connection');
    return Promise.resolve(cableConnection);
  } else {
    console.log('🔗 Existing connection is closed, creating new one...');
    // Importante: limpar a conexão antiga completamente
    if (cableConnection) {
      try {
        cableConnection.disconnect();
      } catch (e) {
        console.log('🔗 Error disconnecting old connection:', e.message);
      }
    }
    cableConnection = null;
    return getWebSocketConnection();
  }
};

// Reconecta o WebSocket com novo token
export const reconnectWebSocket = function() {
  console.log('🔄 Reconnecting WebSocket with fresh token...');
  
  // Fechar conexão existente se houver
  if (cableConnection) {
    cableConnection.disconnect();
    cableConnection = null;
  }

  // Verificar se o token expirou e fazer refresh se necessário
  const tokenPromise = token.isExpired() ? ensureValidToken() : Promise.resolve();
  
  return tokenPromise.then(function() {
    // Criar nova conexão
    return createConnection();
  }).then(function(connection) {
    cableConnection = connection;
    console.log('✅ WebSocket reconnected successfully');
    return cableConnection;
  }).catch(function(error) {
    console.error('❌ Failed to reconnect WebSocket:', error);
    throw error;
  });
};

// Desconecta o WebSocket
export const disconnectWebSocket = function() {
  if (cableConnection) {
    console.log('Disconnecting WebSocket');
    cableConnection.disconnect();
    cableConnection = null;
  }
  
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

// Helper para criar subscription com conexão automática
export const createSubscription = function(channelName, params, callbacks) {
  params = params || {};
  callbacks = callbacks || {};
  
  console.log('📡 Creating subscription to', channelName, 'with params:', params);
  
  return getWebSocketConnection().then(function(cable) {
    // Verificar se a conexão existe (mas não verificar se está aberta ainda)
    if (!cable || !cable.connection) {
      throw new Error('WebSocket connection is not available');
    }

    console.log('🔗 Creating subscription with cable:', cable);
    console.log('🔗 Connection state:', cable.connection.isOpen());

    const subscription = cable.subscriptions.create(
      Object.assign({ channel: channelName }, params),
      {
        connected: function() {
          console.log('📡 Subscribed to ' + channelName);
          if (callbacks.connected) callbacks.connected();
        },
        disconnected: function() {
          console.log('📡 Disconnected from ' + channelName);
          if (callbacks.disconnected) callbacks.disconnected();
        },
        rejected: function() {
          console.log('🚫 Subscription to ' + channelName + ' rejected');
          if (callbacks.rejected) callbacks.rejected();
        },
        received: function(data) {
          console.log('📨 Received data from ' + channelName + ':', data);
          if (callbacks.received) callbacks.received(data);
        }
      }
    );

    // Verificar se a subscription foi criada corretamente
    if (!subscription) {
      throw new Error('Failed to create subscription');
    }

    console.log('✅ Subscription created successfully');
    return subscription;
  }).catch(function(error) {
    console.error('Failed to create subscription to ' + channelName + ':', error);
    throw error;
  });
};